/**
 * Discrete CLI commands, grouped by the same domains the console uses.
 *
 * Every entry names a real MCP tool and maps CLI flags onto that tool's
 * arguments. `carrier ask` stays as the natural-language fallback; these
 * commands are the deterministic path for the same capabilities.
 *
 * Argument names below MUST match the tool inputSchema in src/tools.ts,
 * src/intelligence.ts and src/tools-backlog.ts. A test asserts that every
 * `tool` here is registered by the server.
 *
 * A few capabilities have no MCP tool at all — minting an `ak_` key is the
 * clearest case, since it has to work before the caller holds a credential any
 * tool call could use. Those carry `transport: "rest"` and name a Carrier REST
 * path instead. They stay in this registry so `carrier --help` lists one
 * surface, not two.
 *
 * `carrier onboard` is the other one, for the same reason: it runs before the
 * account has an OCS token, which is what every MCP tool call needs. It has no
 * `portal` verb on purpose — connecting by OCS portal login hands a plaintext
 * portal password to a sandboxed browser, so it stays a console-only flow.
 *
 * The domains above the `<generated:catalog-domains>` sentinel are hand-written
 * and stay that way. Everything between the sentinels comes from
 * `scripts/generate-domains.mjs`, which reads the built server's own catalog, so
 * every registered tool has a command. Edit the generator, not the region, and
 * re-run it whenever the server gains or renames a tool:
 *
 *     pnpm -F @carrierllc/mcp-server build
 *     pnpm -F @carrierllc/mcp generate:domains
 *
 * `catalog-command-parity.test.js` fails if that is ever skipped.
 *
 * This file must stay import-free. Two tests transpile it on its own and load
 * it as a data URL, which cannot resolve a relative import.
 */

/**
 * Flag value types. The two array forms take one comma-separated value, which
 * is how a tool taking `string[]` or `number[]` reaches the command line.
 */
export type CapabilityArgType = "string" | "number" | "boolean" | "string[]" | "number[]";

export interface CapabilityOption {
  /** Commander flag spec, e.g. "--iccid <iccid>". */
  flags: string;
  description: string;
  /** Tool argument name. A dot nests, e.g. "subscriber.iccid". */
  arg: string;
  type: CapabilityArgType;
  required?: boolean;
}

export type RestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/** Shared by both transports. */
interface CapabilityCommandBase {
  /**
   * Subcommand verb, e.g. "list". A single space nests one level, e.g.
   * "sim-status set" registers as `carrier subscribers sim-status set`.
   */
  name: string;
  summary: string;
  options: readonly CapabilityOption[];
  /**
   * Write or admin capability. On the MCP transport this sends dry_run=true
   * unless --commit is passed; on REST, where there is no dry_run field to
   * send, --commit is what makes the request leave the machine at all. Either
   * way a mistyped ICCID costs a preview instead of a live change.
   */
  write?: boolean;
  /**
   * Fills startDate/endDate with the last 7 UTC days when both are omitted,
   * using the shared OCS window helper. OCS rejects wider windows.
   */
  usageWindow?: boolean;
  /**
   * Tool arg names where at least one must be present. Use for commands with
   * documented alternative identifiers (e.g. --iccid vs --account-id) where
   * neither option can be `required` on its own.
   */
  requireOneOf?: readonly string[];
  /**
   * Refuse to run without --confirm. For tools that change something real but
   * carry no dry_run, confirm or confirm_token argument of their own — sending
   * `dry_run` to one of those is worse than useless, because the tool ignores
   * the field and does the thing. The flag is a CLI gate: nothing is added to
   * the arguments, the call simply does not happen without it.
   */
  requireConfirm?: boolean;
}

/** The default: one command, one MCP tool. */
export interface McpCapabilityCommand extends CapabilityCommandBase {
  transport?: "mcp";
  /** MCP tool this verb calls. */
  tool: string;
}

/**
 * A capability the MCP server does not expose, served by `apps/api` instead.
 *
 * `restPath` may carry `{argName}` placeholders; each is filled from the built
 * argument of the same name and removed from the body. Whatever is left goes
 * out as a JSON body, or as a query string on GET and DELETE.
 */
export interface RestCapabilityCommand extends CapabilityCommandBase {
  transport: "rest";
  restMethod: RestMethod;
  /** Path under the API origin, e.g. "/v1/api-keys". Must exist in openapi.ts. */
  restPath: string;
}

export type CapabilityCommand = McpCapabilityCommand | RestCapabilityCommand;

export interface CapabilityDomain {
  name: string;
  summary: string;
  commands: readonly CapabilityCommand[];
}

const ICCID: CapabilityOption = {
  flags: "--iccid <iccid>",
  description: "Subscriber ICCID",
  arg: "iccid",
  type: "string",
  required: true,
};

const ACCOUNT_ID = (description: string): CapabilityOption => ({
  flags: "--account-id <id>",
  description,
  arg: "accountId",
  type: "number",
});

const RESELLER_ID: CapabilityOption = {
  flags: "--reseller-id <id>",
  description: "Reseller ID (omit for the token owner's reseller)",
  arg: "resellerId",
  type: "number",
};

const PACKAGE_ID: CapabilityOption = {
  flags: "--package-id <id>",
  description: "Package ID from `carrier packages list`",
  arg: "packageId",
  type: "number",
  required: true,
};

const TEMPLATE_ID: CapabilityOption = {
  flags: "--template-id <id>",
  description: "Template ID from `carrier templates list`",
  arg: "templateId",
  type: "number",
  required: true,
};

const PERIOD: readonly CapabilityOption[] = [
  {
    flags: "--start <date>",
    description: "Start date YYYY-MM-DD (default: 7 days ago)",
    arg: "startDate",
    type: "string",
  },
  {
    flags: "--end <date>",
    description: "End date YYYY-MM-DD, max 7 days from start (default: today)",
    arg: "endDate",
    type: "string",
  },
];

export const CLI_DOMAINS: readonly CapabilityDomain[] = [
  {
    name: "subscribers",
    summary: "Look up, inspect and change subscribers.",
    commands: [
      {
        name: "list",
        tool: "list_subscribers",
        summary: "List subscribers. OCS needs at least one of account/iccid/msisdn/imsi.",
        options: [
          ACCOUNT_ID("Filter by account ID"),
          { flags: "--iccid <iccid>", description: "Filter by ICCID", arg: "iccid", type: "string" },
          { flags: "--msisdn <msisdn>", description: "Filter by MSISDN", arg: "msisdn", type: "string" },
          { flags: "--imsi <imsi>", description: "Filter by IMSI", arg: "imsi", type: "string" },
          {
            flags: "--activation-code <code>",
            description: "Filter by eSIM activation code",
            arg: "activationCode",
            type: "string",
          },
          { flags: "--status <status>", description: "Filter by status, e.g. ACTIVE", arg: "status", type: "string" },
          { flags: "--limit <n>", description: "Max rows to return", arg: "limit", type: "number" },
          { flags: "--offset <n>", description: "Pagination offset", arg: "offset", type: "number" },
        ],
        // OCS rejects an unfiltered list. Catch it here so the user gets the
        // flags to pass instead of a raw schema-validation dump from the tool.
        requireOneOf: ["accountId", "iccid", "msisdn", "imsi", "activationCode"],
      },
      {
        name: "get",
        tool: "get_subscriber",
        summary: "Full record for one subscriber by ICCID or MSISDN.",
        options: [
          { flags: "--iccid <iccid>", description: "Subscriber ICCID", arg: "iccid", type: "string" },
          { flags: "--msisdn <msisdn>", description: "Subscriber MSISDN", arg: "msisdn", type: "string" },
          {
            flags: "--gz-counter",
            description: "Include the green-zone byte counter",
            arg: "with_gz_counter",
            type: "boolean",
          },
        ],
      },
      {
        name: "location",
        tool: "get_subscriber_location",
        summary: "Last-known location from the most recent cell tower usage.",
        options: [ICCID],
      },
      {
        name: "usage",
        tool: "subscriber_usage",
        summary: "Daily data, voice and SMS usage over a window of up to 7 days.",
        options: [ICCID, ...PERIOD],
        usageWindow: true,
      },
      {
        name: "events",
        tool: "subscriber_network_events",
        summary: "Network attach/detach events over a window of up to 7 days.",
        options: [ICCID, ...PERIOD],
        usageWindow: true,
      },
      {
        name: "active-period",
        tool: "subscriber_active_period",
        summary: "First and last usage dates for one subscriber.",
        options: [ICCID],
      },
      {
        name: "esim-status",
        tool: "esim_status_per_account",
        summary: "eSIM state counts per account: active, suspended, inventory.",
        options: [ACCOUNT_ID("Report on a single account"), RESELLER_ID],
      },
      {
        name: "set-status",
        tool: "modify_subscriber_status",
        summary: "Change a subscriber's status.",
        options: [
          ICCID,
          {
            flags: "--status <status>",
            description: "New status, e.g. ACTIVE or SUSPENDED",
            arg: "status",
            type: "string",
            required: true,
          },
        ],
        write: true,
        requireConfirm: true,
      },
      {
        name: "set-balance",
        tool: "modify_subscriber_balance",
        summary: "Add to or replace a subscriber's balance.",
        options: [
          ICCID,
          {
            flags: "--amount <amount>",
            description: "Amount to add (adapt) or set to (set)",
            arg: "amount",
            type: "number",
            required: true,
          },
          {
            flags: "--mode <mode>",
            description: "'adapt' adds or subtracts, 'set' replaces",
            arg: "mode",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "sim-status show",
        tool: "get_sim_provider_status",
        summary: "SIM/eSIM status at the SIM provider level, distinct from OCS status.",
        options: [ICCID],
      },
      {
        name: "sim-status set",
        tool: "change_sim_status",
        summary: "Change the SIM/eSIM status at the SIM provider level. ENABLED and DISABLED map to OCS Affected and Free.",
        options: [
          ICCID,
          {
            flags: "--status <status>",
            description: "New SIM status: ENABLED or DISABLED",
            arg: "simStatus",
            type: "string",
            required: true,
          },
        ],
        write: true,
        requireConfirm: true,
      },
      {
        name: "phone-number set",
        tool: "affect_subscriber_phone_number",
        summary: "Assign a phone number (MSISDN) to a subscriber.",
        options: [
          ICCID,
          {
            flags: "--phone-number <e164>",
            description: "E.164 phone number to assign, e.g. +31612345678",
            arg: "phone_number",
            type: "string",
            required: true,
          },
          {
            flags: "--phone-type <type>",
            description: "'fake' for test/dev MSISDNs, 'real' for production",
            arg: "phone_type",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "contact set",
        tool: "modify_subscriber_contact_info",
        summary: "Update contact details on a subscriber. Omitted fields stay unchanged.",
        options: [
          ICCID,
          { flags: "--first-name <name>", description: "First name", arg: "firstName", type: "string" },
          { flags: "--last-name <name>", description: "Last name", arg: "lastName", type: "string" },
          { flags: "--company <name>", description: "Company name", arg: "company", type: "string" },
          { flags: "--email <email>", description: "Email address", arg: "email", type: "string" },
          { flags: "--phone-number <number>", description: "Phone number", arg: "phoneNumber", type: "string" },
        ],
        write: true,
      },
      {
        name: "mobile-plan set",
        tool: "modify_subscriber_mobile_plan",
        summary: "Change the mobile pricing plan a subscriber is billed under.",
        options: [
          ICCID,
          {
            flags: "--plan-id <id>",
            description: "Mobile pricing plan ID",
            arg: "mobile_plan_id",
            type: "number",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "voip-plan set",
        tool: "modify_subscriber_voip_plan",
        summary: "Change the VoIP pricing plan assigned to a subscriber.",
        options: [
          ICCID,
          {
            flags: "--plan-id <id>",
            description: "VoIP pricing plan ID",
            arg: "voip_plan_id",
            type: "number",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "traffic set",
        tool: "set_subscriber_traffic_restrictions",
        summary: "Enable or disable data, calls and SMS per traffic type. Omit a flag to leave it unchanged.",
        options: [
          ICCID,
          { flags: "--data <bool>", description: "Allow data traffic (true or false)", arg: "dataAllowed", type: "boolean" },
          { flags: "--moc <bool>", description: "Allow outbound calls (true or false)", arg: "mocAllowed", type: "boolean" },
          { flags: "--mtc <bool>", description: "Allow inbound calls (true or false)", arg: "mtcAllowed", type: "boolean" },
          { flags: "--sms <bool>", description: "Allow outbound SMS (true or false)", arg: "smsMoAllowed", type: "boolean" },
        ],
        write: true,
      },
      {
        name: "bitrate show",
        tool: "hlr_get_bitrate",
        summary: "Current HLR-level bandwidth cap for a subscriber.",
        options: [ICCID],
      },
      {
        name: "bitrate set",
        tool: "hlr_set_bitrate",
        summary: "Set an HLR-level bandwidth cap. Bits per second are mapped onto the OCS KB_ enum.",
        options: [
          ICCID,
          {
            flags: "--bitrate <bps>",
            description: "Max bitrate in bits per second. 256000 is KB_256. 0 is UNLIMITED.",
            arg: "bitrate",
            type: "number",
          },
          {
            flags: "--bitrate-string <level>",
            description: "OCS bitrate level, e.g. KB_256, KB_1024, UNLIMITED (instead of --bitrate)",
            arg: "bitrate_string",
            type: "string",
          },
        ],
        write: true,
        requireOneOf: ["bitrate", "bitrate_string"],
      },
      {
        name: "greenzone reset",
        tool: "reset_subscriber_gz_counter",
        summary: "Reset the Green Zone byte counter to zero. Irreversible.",
        options: [ICCID],
        write: true,
        requireConfirm: true,
      },
      {
        name: "cell-location",
        tool: "get_subscriber_location_by_cell_id",
        summary: "Resolve a raw cell tower tuple to coordinates (Bridge4IP GeoSense).",
        options: [
          {
            flags: "--radio-type <type>",
            description: "Radio type: 2G, 3G, 4G, 5G or NB-IoT",
            arg: "radio_type",
            type: "string",
            required: true,
          },
          { flags: "--mcc <mcc>", description: "Mobile Country Code", arg: "mcc", type: "number", required: true },
          { flags: "--mnc <mnc>", description: "Mobile Network Code", arg: "mnc", type: "number", required: true },
          { flags: "--lac <lac>", description: "Location Area Code", arg: "lac", type: "number", required: true },
          { flags: "--cell-id <id>", description: "Cell tower ID (strongly recommended)", arg: "cell_id", type: "number" },
          {
            flags: "--signal-strength <dbm>",
            description: "Signal strength in dBm, e.g. -89 (improves accuracy)",
            arg: "signal_strength",
            type: "number",
          },
        ],
      },
    ],
  },
  {
    name: "packages",
    summary: "Packages assigned to a subscriber.",
    commands: [
      {
        name: "list",
        tool: "list_subscriber_packages",
        summary: "Packages on one subscriber, with allowance and expiry.",
        options: [ICCID],
      },
      {
        name: "assign",
        tool: "assign_package",
        summary: "Assign a one-time package from a template.",
        options: [
          { ...ICCID, required: false },
          {
            flags: "--template-id <id>",
            description: "Package template ID from `carrier templates list`",
            arg: "packageTemplateId",
            type: "number",
            required: true,
          },
          {
            flags: "--account-id <id>",
            description: "Auto-pick a free eSIM from this account instead of using --iccid",
            arg: "account_for_subs",
            type: "number",
          },
        ],
        write: true,
        requireOneOf: ["iccid", "account_for_subs"],
      },
      {
        name: "set-status",
        tool: "modify_package_status",
        summary: "Activate or deactivate one package without deleting it.",
        options: [
          ICCID,
          PACKAGE_ID,
          {
            flags: "--status <status>",
            description: "New package status, e.g. ACTIVE or INACTIVE",
            arg: "status",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "set-expiry",
        tool: "modify_package_expiry",
        summary: "Move a package's expiry date. Pass an absolute date or a day count.",
        options: [
          ICCID,
          PACKAGE_ID,
          {
            flags: "--expires <date>",
            description: "Absolute expiry, ISO 8601 (e.g. 2026-06-01)",
            arg: "expirationDate",
            type: "string",
          },
          {
            flags: "--validity-days <n>",
            description: "Days from now until expiry (instead of --expires)",
            arg: "validity_days",
            type: "number",
          },
        ],
        write: true,
      },
      {
        name: "set-limits",
        tool: "modify_package_limits",
        summary: "Change data, voice or SMS ceilings on an assigned package.",
        options: [
          ICCID,
          PACKAGE_ID,
          {
            flags: "--limits <json>",
            description: 'New limits as JSON, e.g. \'{"dataLimit":5368709120}\'',
            arg: "limits",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "assign-recurring",
        tool: "assign_recurring_package",
        summary: "Assign an auto-renewing package from a recurring-enabled template.",
        options: [
          ICCID,
          {
            flags: "--template-id <id>",
            description: "Recurring package template ID from `carrier templates list`",
            arg: "packageTemplateId",
            type: "number",
            required: true,
          },
          {
            flags: "--activate-at-first-use",
            description: "Activate on first network usage (instead of --start-time)",
            arg: "activation_at_first_use",
            type: "boolean",
          },
          {
            flags: "--start-time <iso>",
            description: "Scheduled activation, ISO 8601 UTC (instead of --activate-at-first-use)",
            arg: "start_time_utc",
            type: "string",
          },
        ],
        write: true,
      },
      {
        name: "recurring set",
        tool: "stop_resume_recurring_package",
        summary: "Pause or restart the auto-renewal of a recurring package.",
        options: [
          ICCID,
          PACKAGE_ID,
          {
            flags: "--action <action>",
            description: "'stop' halts future renewals, 'resume' re-enables them",
            arg: "action",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "active-period set",
        tool: "modify_subscriber_package_active_period",
        summary: "Move the start and/or end of a package's active period.",
        options: [
          ICCID,
          {
            flags: "--package-id <id>",
            description: "Package ID from `carrier packages list`",
            arg: "package_id",
            type: "number",
            required: true,
          },
          {
            flags: "--start <date>",
            description: "New start date, ISO 8601 (e.g. 2026-09-01)",
            arg: "start_date",
            type: "string",
          },
          {
            flags: "--end <date>",
            description: "New end date, ISO 8601 (e.g. 2026-09-30)",
            arg: "end_date",
            type: "string",
          },
          {
            flags: "--comment <text>",
            description: "Audit note stored on the package (OCS requires one; a default is sent)",
            arg: "comment",
            type: "string",
          },
        ],
        write: true,
        requireOneOf: ["start_date", "end_date"],
      },
      {
        name: "clean",
        tool: "clean_all_packages",
        summary: "Remove ALL packages from a subscriber. Irreversible.",
        options: [ICCID],
        write: true,
        requireConfirm: true,
      },
      {
        name: "delete",
        tool: "delete_subscriber_package",
        summary: "Permanently remove one package from a subscriber.",
        options: [ICCID, PACKAGE_ID],
        write: true,
        requireConfirm: true,
      },
    ],
  },
  {
    name: "templates",
    summary: "The package template catalog.",
    commands: [
      {
        name: "list",
        tool: "list_package_templates",
        summary: "Browse package templates available for assignment.",
        // No --account-id. OCS's listPrepaidPackageTemplate rejects `accountId`
        // outright — "Unrecognized field \"accountId\" (5 known properties:
        // locationZoneId, templateId, destinationListId, resellerId, sponsorId)"
        // — so the only filter flag this command ever advertised was 100%
        // broken. Both tool surfaces already dropped it (see the note in
        // apps/mcp-server/src/tools.ts): accounts are a different id space from
        // resellers and sponsors, so mapping it onto one of those would turn a
        // loud error into another tenant's catalog returned silently.
        options: [],
      },
      {
        name: "create",
        tool: "create_package_template",
        summary: "Create a package template from a JSON configuration.",
        options: [
          {
            flags: "--template <json>",
            description: 'Template config as JSON, e.g. \'{"name":"Europe 5GB","dataLimit":5368709120}\'',
            arg: "template",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "set-core",
        tool: "modify_template_core",
        summary: "Change name, limits, cost, validity or zone on a template.",
        options: [
          TEMPLATE_ID,
          {
            flags: "--changes <json>",
            description: 'Core fields as JSON, e.g. \'{"name":"Europe 5GB"}\'',
            arg: "changes",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "set-recurring",
        tool: "modify_template_recurring",
        summary: "Change auto-renewal settings on a template.",
        options: [
          TEMPLATE_ID,
          {
            flags: "--changes <json>",
            description: 'Recurring fields as JSON, e.g. \'{"recurringPeriodicityType":2,"nbOccurrence":12}\'',
            arg: "changes",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "set-throttling",
        tool: "modify_template_throttling",
        summary: "Change throttling thresholds. Applies to existing packages too.",
        options: [
          TEMPLATE_ID,
          {
            flags: "--changes <json>",
            description: 'Throttling fields as JSON, e.g. \'{"throttlingActive":true}\'',
            arg: "changes",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
    ],
  },
  {
    name: "zones",
    summary: "Location zones and destination lists.",
    commands: [
      {
        name: "list",
        tool: "list_detailed_location_zones",
        summary: "Location zones with countries and operators. Prefer this over `elements`.",
        options: [
          RESELLER_ID,
          {
            flags: "--verbose <bool>",
            description: "Return the full OCS zone shape instead of the compact projection",
            arg: "verbose",
            type: "boolean",
          },
        ],
      },
      {
        name: "elements",
        tool: "list_location_zones",
        summary: "Raw zone elements. Upstream OCS returns malformed rows for some zones.",
        options: [
          {
            flags: "--zone-id <id>",
            description: "Filter to one location zone",
            arg: "locationZoneId",
            type: "number",
            required: true,
          },
        ],
      },
      {
        name: "create",
        tool: "create_location_zone",
        summary: "Create a location zone from a JSON configuration.",
        options: [
          {
            flags: "--zone <json>",
            description:
              'Zone config as JSON, e.g. \'{"locationZoneName":"Europe","tadigList":["AZER1","QWER1"],"networkProfileId":79}\'',
            arg: "zone",
            type: "string",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "network-profile set",
        tool: "change_network_profile_of_location_zone",
        summary: "Attach or change the network profile on an existing location zone.",
        options: [
          {
            flags: "--zone-id <id>",
            description: "Location zone ID from `carrier zones list`",
            arg: "location_zone_id",
            type: "number",
            required: true,
          },
          {
            flags: "--network-profile-id <id>",
            description: "Network profile ID from `carrier network-profiles list`",
            arg: "network_profile_id",
            type: "number",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "destinations",
        tool: "list_destination_lists",
        summary: "Destination list catalog for voice and SMS packages.",
        options: [RESELLER_ID],
      },
      {
        name: "prefixes",
        tool: "list_destination_prefixes",
        summary: "Dialling prefixes inside one destination list.",
        options: [
          {
            flags: "--destination-list-id <id>",
            description: "Destination list ID from `carrier zones destinations`",
            arg: "destinationListId",
            type: "number",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "steering",
    summary: "Network steering lists and operator preference.",
    commands: [
      {
        name: "list",
        tool: "list_steering_lists",
        summary: "Steering lists configured for this reseller.",
        options: [RESELLER_ID],
      },
      {
        name: "assign",
        tool: "modify_subscriber_steering_list",
        summary: "Assign a steering list to a subscriber. Follow with `push`.",
        options: [
          ICCID,
          {
            flags: "--list-id <id>",
            description: "Steering list ID from `carrier steering list`",
            arg: "steeringListId",
            type: "number",
            required: true,
          },
        ],
        write: true,
      },
      {
        name: "push",
        tool: "push_steering_to_subscriber",
        summary: "Push the assigned operator preference list to the device now.",
        options: [ICCID],
        write: true,
      },
    ],
  },
  {
    name: "accounts",
    summary: "Reseller accounts: balances and subscriber moves.",
    commands: [
      {
        name: "list",
        tool: "list_reseller_accounts",
        summary: "Accounts under a reseller with name, balance and type.",
        options: [RESELLER_ID],
      },
      {
        name: "balance set",
        tool: "modify_account_balance",
        summary: "Add to or replace an account's monetary balance.",
        options: [
          { ...ACCOUNT_ID("Account ID from `carrier accounts list`"), required: true },
          {
            flags: "--amount <amount>",
            description: "Amount to add (adapt) or set to (set)",
            arg: "amount",
            type: "number",
            required: true,
          },
          {
            flags: "--mode <mode>",
            description: "'adapt' adds or subtracts, 'set' replaces",
            arg: "mode",
            type: "string",
            required: true,
          },
        ],
        write: true,
        requireConfirm: true,
      },
      {
        name: "move-subscribers",
        tool: "move_subscriber_range_to_account",
        summary: "Move a contiguous IMSI or ICCID range of subscribers to another account.",
        options: [
          {
            flags: "--src-account-id <id>",
            description: "Source account ID from `carrier accounts list`",
            arg: "srcAccountId",
            type: "number",
            required: true,
          },
          {
            flags: "--dest-account <id>",
            description: "Destination account ID from `carrier accounts list`",
            arg: "destAccount",
            type: "number",
            required: true,
          },
          {
            flags: "--range-type <type>",
            description: "Whether --range-start/--range-end are IMSI or ICCID ('IMSI' | 'ICCID')",
            arg: "rangeType",
            type: "string",
            required: true,
          },
          {
            flags: "--range-start <value>",
            description: "Inclusive start of the identifier range",
            arg: "rangeStart",
            type: "string",
            required: true,
          },
          {
            flags: "--range-end <value>",
            description: "Inclusive end of the identifier range",
            arg: "rangeEnd",
            type: "string",
            required: true,
          },
        ],
        write: true,
        requireConfirm: true,
      },
    ],
  },
  {
    name: "reseller",
    summary: "The reseller the token belongs to.",
    commands: [
      {
        name: "show",
        tool: "get_reseller_info",
        summary: "Full reseller record: balance, pricing plans, contact and traffic config.",
        options: [RESELLER_ID],
      },
    ],
  },
  {
    name: "tariff",
    summary: "Wholesale tariffs: mobile and VoIP rates.",
    commands: [
      {
        name: "show",
        tool: "get_tariff",
        summary: "Per-country wholesale rates for data, voice and SMS.",
        options: [
          RESELLER_ID,
          {
            flags: "--country <name|iso2>",
            description: "Filter to one country: ISO2 (\"nl\") or name (\"Netherlands\")",
            arg: "country",
            type: "string",
          },
          {
            flags: "--traffic-type <type>",
            description: "Filter to data, voice or sms",
            arg: "trafficType",
            type: "string",
          },
          {
            flags: "--verbose <bool>",
            description: "Return the full OCS rule shape instead of the compact projection",
            arg: "verbose",
            type: "boolean",
          },
        ],
      },
      {
        name: "voip",
        tool: "list_subscriber_voip_tariff",
        summary: "VoIP tariff catalog for a reseller.",
        options: [
          {
            flags: "--reseller-id <id>",
            description: "Reseller ID (omit for the token owner's reseller)",
            arg: "reseller_id",
            type: "number",
          },
        ],
      },
      {
        name: "voip-rules",
        tool: "list_voip_tariff_rule",
        summary: "Rate rules inside one VoIP plan.",
        options: [
          {
            flags: "--voip-plan-id <id>",
            description: "VoIP plan ID from `carrier tariff voip`",
            arg: "voip_plan_id",
            type: "number",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "sms",
    summary: "Mobile-terminated SMS to subscribers.",
    commands: [
      {
        name: "send",
        tool: "send_sms",
        summary: "Send an MT SMS to one subscriber.",
        options: [
          ICCID,
          {
            flags: "--msisdn <msisdn>",
            description: "Target MSISDN in E.164 format",
            arg: "msisdn",
            type: "string",
            required: true,
          },
          {
            flags: "--message <text>",
            description: "SMS text (160 chars GSM-7; 70 with emoji or non-Latin scripts)",
            arg: "message",
            type: "string",
            required: true,
          },
          {
            flags: "--sender <sender>",
            description: "Sender ID or number shown on the device",
            arg: "sender",
            type: "string",
          },
        ],
        write: true,
      },
    ],
  },
  {
    name: "sponsors",
    summary: "Sponsor networks backing the eSIM profiles.",
    commands: [
      {
        name: "list",
        tool: "list_sponsors",
        summary: "Sponsor carriers available to this reseller.",
        options: [RESELLER_ID],
      },
    ],
  },
  {
    name: "network-profiles",
    summary: "Roaming configuration profiles for zones and provisioning.",
    commands: [
      {
        name: "list",
        tool: "list_network_profiles",
        summary: "Network profiles available to this reseller.",
        options: [],
      },
    ],
  },
  {
    name: "webhooks",
    summary: "Bridge4IP webhook and relay flag status.",
    commands: [
      {
        name: "show",
        tool: "carrier_webhook_config",
        summary: "Current relay flags (LU, Gy, VoIP, calls+SMS) and notification webhooks.",
        options: [
          {
            flags: "--reseller-id <id>",
            description: "Reseller ID (omit for the token owner's reseller)",
            arg: "reseller_id",
            type: "number",
          },
        ],
      },
    ],
  },
  {
    name: "intelligence",
    summary: "Composite reports over the fleet.",
    commands: [
      {
        name: "fleet-health",
        tool: "fleet_health",
        summary: "eSIM counts, low-balance accounts and what needs attention.",
        options: [ACCOUNT_ID("Report on a single account")],
      },
      {
        name: "diagnose",
        tool: "diagnose_subscriber",
        summary: "Why one subscriber is offline, throttled or failing to attach.",
        options: [ICCID],
      },
      {
        name: "anomalies",
        tool: "detect_usage_anomalies",
        summary: "Usage spikes and burn rates that exhaust the package early.",
        options: [ICCID],
      },
      {
        name: "churn",
        tool: "churn_risk",
        summary: "Churn risk score with contributing factors.",
        options: [ICCID],
      },
      {
        name: "optimize",
        tool: "optimize_package",
        summary: "Better-fitting package for one subscriber, with the saving.",
        options: [ICCID],
      },
      {
        name: "coverage",
        tool: "audit_network_coverage",
        summary: "Networks subscribers actually attach to, against your steering lists.",
        options: [
          ACCOUNT_ID("Report on a single account"),
          { flags: "--limit <n>", description: "Subscribers to sample (default 50)", arg: "limit", type: "number" },
        ],
      },
      {
        name: "high-cost",
        tool: "high_cost_subscribers",
        summary: "Subscribers whose consumption erodes the margin.",
        options: [
          ACCOUNT_ID("Report on a single account"),
          { flags: "--limit <n>", description: "Subscribers to analyse (default 100)", arg: "limit", type: "number" },
          {
            flags: "--threshold-pct <n>",
            description: "Usage percentage that flags a subscriber (default 80)",
            arg: "thresholdPct",
            type: "number",
          },
        ],
      },
      {
        name: "marketing",
        tool: "marketing_intelligence",
        summary: "Where subscribers and revenue concentrate, and which markets grow.",
        options: [ACCOUNT_ID("Report on a single account")],
      },
      {
        name: "country-entry",
        tool: "detect_country_entry",
        summary: "Country a subscriber is in now, optionally diffed against the last known one.",
        options: [
          { ...ICCID, arg: "subscriber.iccid" },
          {
            flags: "--expected-country <iso2>",
            description: "Last known ISO 3166-1 alpha-2 country, e.g. NL",
            arg: "expectedCountry",
            type: "string",
          },
        ],
      },
    ],
  },
  {
    name: "api-keys",
    summary: "Mint Carrier API keys (ak_…) for headless and CI use",
    commands: [
      {
        name: "create",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/api-keys",
        summary:
          "Create an org API key. The secret is shown once and never stored by the CLI.",
        options: [
          {
            flags: "--name <name>",
            description: "Label for the key, max 64 characters",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--description <text>",
            description: "Optional note, max 256 characters",
            arg: "description",
            type: "string",
          },
          {
            flags: "--seconds-until-expiration <n>",
            description: "Expiry in seconds, 60 to 31536000. Omit for a key that does not expire.",
            arg: "secondsUntilExpiration",
            type: "number",
          },
        ],
      },
    ],
  },
  {
    name: "onboard",
    summary: "Connect a new account to OCS without opening a browser",
    commands: [
      {
        name: "status",
        transport: "rest",
        restMethod: "GET",
        restPath: "/v1/onboarding",
        summary: "Show how far onboarding has got. Same state the console renders.",
        options: [],
      },
      {
        name: "org",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/org",
        summary: "Record who the operator is (step 1).",
        options: [
          {
            flags: "--name <name>",
            description: "Operator name, max 128 characters",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--website <url>",
            description: "Public site, if there is one",
            arg: "website",
            type: "string",
          },
          {
            flags: "--country <iso2>",
            description: "ISO 3166-1 alpha-2 country, e.g. NL",
            arg: "country",
            type: "string",
          },
          {
            flags: "--operator-type <type>",
            description: "How the operator describes itself, e.g. mvno",
            arg: "operatorType",
            type: "string",
          },
        ],
      },
      {
        name: "connect",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/ocs-connect",
        summary:
          "Connect your own eSIMVault token (step 2, BYO). Verified against OCS, then stored encrypted; the token is never echoed back.",
        options: [
          {
            flags: "--token <token>",
            description:
              "eSIMVault API token. A flag lands in shell history — prefer a leading space, or run this once from a fresh shell.",
            arg: "token",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "managed",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/managed-account",
        summary:
          "Let Carrier provision the OCS sub-account instead (step 2, the primary path). Idempotent, and clears any BYO token.",
        options: [],
      },
      {
        name: "plan",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/plan",
        summary: "Record the starting plan template (step 3).",
        options: [
          {
            flags: "--template-id <id>",
            description: "Package template to start from",
            arg: "templateId",
            type: "string",
            required: true,
          },
          {
            flags: "--display-name <name>",
            description: "What customers will see the plan called",
            arg: "displayName",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "plan-skip",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/plan/skip",
        summary: "Skip the plan step and come back to it later (step 3).",
        options: [],
      },
      {
        name: "test-esim",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/test-esim",
        summary:
          "Read back one eSIM from the connected fleet (step 4). Nothing is provisioned; a demo ICCID with a reason comes back if OCS has nothing to show.",
        options: [],
      },
      {
        name: "complete",
        transport: "rest",
        restMethod: "POST",
        restPath: "/v1/onboarding/complete",
        summary: "Mark onboarding done (step 5).",
        options: [],
      },
    ],
  },
  // <generated:catalog-domains>
  // Generated from the MCP catalog by scripts/generate-domains.mjs. Do not edit by hand:
  // rebuild the server, re-run the generator, and review the diff.
  {
    name: "billing",
    summary: "Carrier plan credits, spend thresholds and rate-limit state",
    commands: [
      {
        name: "events",
        tool: "billing_events",
        summary: "View recent billing threshold events: notifications, invoice triggers, hard cap alerts, and overage pauses.",
        options: [
          {
            flags: "--limit <n>",
            description: "Maximum number of events to return (default: 20, max: 50).",
            arg: "limit",
            type: "number",
          },
        ],
      },
      {
        name: "configure",
        tool: "configure_billing",
        summary: "Configure your billing preferences: enable/disable overages, set billing thresholds (bill shock prevention), configure notification percentages, and set hard spending caps.",
        requireConfirm: true,
        options: [
          {
            flags: "--overages-enabled <bool>",
            description: "Enable or disable auto-billed overages when monthly credits are exhausted.",
            arg: "overages_enabled",
            type: "boolean",
          },
          {
            flags: "--billing-threshold-cents <n>",
            description: "Amount in cents at which an invoice is automatically generated (e.g., 10000 = $100).",
            arg: "billing_threshold_cents",
            type: "number",
          },
          {
            flags: "--hard-cap-cents <n>",
            description: "Maximum overage spend in cents before overages are paused (0 = no cap).",
            arg: "hard_cap_cents",
            type: "number",
          },
          {
            flags: "--auto-pause-on-cap <bool>",
            description: "Whether to automatically pause overages when hard cap is reached.",
            arg: "auto_pause_on_cap",
            type: "boolean",
          },
          {
            flags: "--notify-at-pct <list>",
            description: "Percentage thresholds at which to send notifications (e.g., [50, 80, 95, 100]). Comma-separated.",
            arg: "notify_at_pct",
            type: "number[]",
          },
          {
            flags: "--notification-email <email>",
            description: "Email address for billing threshold notifications.",
            arg: "notification_email",
            type: "string",
          },
          {
            flags: "--notification-phone <phone>",
            description: "E.164 phone number for SMS billing threshold notifications via Twilio (e.g. +14155552671).",
            arg: "notification_phone",
            type: "string",
          },
          {
            flags: "--webhook-url <url>",
            description: "Webhook URL for billing threshold events.",
            arg: "webhook_url",
            type: "string",
          },
        ],
      },
      {
        name: "credits",
        tool: "credit_balance",
        summary: "Read how much of this Carrier MCP plan is left: credits remaining, daily free credits, overage state, volume discount tier, and billing threshold state.",
        options: [],
      },
      {
        name: "plans",
        tool: "pricing_plans",
        summary: "View all available Carrier MCP pricing plans with credit allotments, features, overage rates, and volume discount tiers.",
        options: [],
      },
      {
        name: "projection",
        tool: "usage_projection",
        summary: "Project your credit usage and costs for the remainder of the billing period based on current consumption rate.",
        options: [
          {
            flags: "--days-to-project <n>",
            description: "Number of days to project forward (default: remaining days in month).",
            arg: "days_to_project",
            type: "number",
          },
        ],
      },
      {
        name: "rate-limit",
        tool: "rate_limit_status",
        summary: "Inspect Bridge4IP OCS rate-limit governor state — bucket fill levels, per-endpoint limits, and 80% alert thresholds.",
        options: [
          {
            flags: "--endpoint <endpoint>",
            description: "OCS method name in camelCase (e.g. 'subscriberUsageOverPeriod').",
            arg: "endpoint",
            type: "string",
          },
        ],
      },
    ],
  },
  {
    name: "wallet",
    summary: "The Carrier prepaid wallet: balance and top-ups",
    commands: [
      {
        name: "balance",
        tool: "wallet_balance",
        summary: "Read the caller organisation's Carrier prepaid wallet: current balance and auto-top-up settings.",
        options: [],
      },
      {
        name: "topup",
        tool: "wallet_topup_checkout",
        summary: "Create a Stripe Checkout link so a human can top up the Carrier prepaid wallet by card.",
        options: [
          {
            flags: "--pack <pack>",
            description: "Pack id (pack_500/pack_1000/pack_2500/pack_5000) or exact EUR-cents amount.",
            arg: "pack",
            type: "string",
          },
        ],
      },
      {
        name: "auto-topup",
        tool: "wallet_auto_topup",
        summary: "Charge the organisation's saved card off-session, right now, and credit the Carrier prepaid wallet with the pack.",
        requireConfirm: true,
        options: [
          {
            flags: "--pack-cents <n>",
            description: "Override pack amount in EUR cents (must match a catalog pack).",
            arg: "pack_cents",
            type: "number",
          },
        ],
      },
    ],
  },
  {
    name: "stripe",
    summary: "Money in the operator's connected Stripe account",
    commands: [
      {
        name: "balance",
        tool: "stripe_connect_balance",
        summary: "Read the money sitting in the operator's connected Stripe account: available and pending, split per currency.",
        options: [],
      },
      {
        name: "payouts",
        tool: "stripe_connect_payouts",
        summary: "List money Stripe has already sent from the operator's connected account to their bank, newest first.",
        options: [
          {
            flags: "--limit <n>",
            description: "Number of payouts to return. Default 10.",
            arg: "limit",
            type: "number",
          },
          {
            flags: "--status <status>",
            description: "Filter by payout status. One of: pending, paid, failed, canceled, in_transit.",
            arg: "status",
            type: "string",
          },
        ],
      },
      {
        name: "disputes",
        tool: "stripe_connect_dispute_list",
        summary: "List chargebacks customers have raised with their bank against the operator's connected Stripe account.",
        options: [
          {
            flags: "--limit <n>",
            description: "limit (number). Default 10.",
            arg: "limit",
            type: "number",
          },
          {
            flags: "--status <status>",
            description: "Filter by dispute status (e.g. needs_response, under_review).",
            arg: "status",
            type: "string",
          },
        ],
      },
      {
        name: "refund",
        tool: "stripe_connect_refund",
        summary: "Refund money to a customer on one charge in the operator's connected Stripe account.",
        options: [
          {
            flags: "--charge-id <id>",
            description: "Stripe charge ID (ch_...).",
            arg: "charge_id",
            type: "string",
            required: true,
          },
          {
            flags: "--amount-cents <n>",
            description: "Partial refund amount in cents.",
            arg: "amount_cents",
            type: "number",
          },
          {
            flags: "--reason <reason>",
            description: "reason (string). One of: duplicate, fraudulent, requested_by_customer.",
            arg: "reason",
            type: "string",
          },
          {
            flags: "--confirm-token <token>",
            description: "Confirmation token from previous call.",
            arg: "confirm_token",
            type: "string",
          },
        ],
      },
    ],
  },
  {
    name: "radar",
    summary: "Stripe Radar fraud reviews and value lists",
    commands: [
      {
        name: "reviews",
        tool: "radar_review_list",
        summary: "List the Stripe Radar fraud reviews waiting on a manual approve/decline decision, newest first.",
        options: [
          {
            flags: "--open-only <bool>",
            description: "If true, only returns open (undecided) reviews. Default true.",
            arg: "open_only",
            type: "boolean",
          },
          {
            flags: "--limit <n>",
            description: "limit (number). Default 10.",
            arg: "limit",
            type: "number",
          },
        ],
      },
      {
        name: "approve",
        tool: "radar_review_approve",
        summary: "Clear a Stripe Radar fraud review so the held charge is captured and the money is taken.",
        options: [
          {
            flags: "--review-id <id>",
            description: "Stripe Radar review ID (prv_...).",
            arg: "review_id",
            type: "string",
            required: true,
          },
          {
            flags: "--confirm-token <token>",
            description: "confirm token (string).",
            arg: "confirm_token",
            type: "string",
          },
        ],
      },
      {
        name: "decline",
        tool: "radar_review_decline",
        summary: "Close a Stripe Radar fraud review as fraudulent, so the held charge is blocked and the customer is never billed.",
        options: [
          {
            flags: "--review-id <id>",
            description: "review id (string).",
            arg: "review_id",
            type: "string",
            required: true,
          },
          {
            flags: "--confirm-token <token>",
            description: "confirm token (string).",
            arg: "confirm_token",
            type: "string",
          },
        ],
      },
      {
        name: "rule-toggle",
        tool: "radar_rule_toggle",
        summary: "Report a Stripe Radar rule's state. Stripe exposes no API to change it, so this changes nothing.",
        options: [
          {
            flags: "--rule-id <id>",
            description: "Stripe Radar rule ID.",
            arg: "rule_id",
            type: "string",
            required: true,
          },
          {
            flags: "--enabled <bool>",
            description: "true = enable, false = disable.",
            arg: "enabled",
            type: "boolean",
            required: true,
          },
        ],
      },
      {
        name: "value-list add",
        tool: "radar_value_list_add",
        summary: "Add one value to an existing Stripe Radar value list, changing how every future charge is scored.",
        options: [
          {
            flags: "--value-list-id <id>",
            description: "Stripe Radar value list ID (rsl_...).",
            arg: "value_list_id",
            type: "string",
            required: true,
          },
          {
            flags: "--value <value>",
            description: "The value to add (email, IP address, country code, etc.).",
            arg: "value",
            type: "string",
            required: true,
          },
          {
            flags: "--confirm-token <token>",
            description: "confirm token (string).",
            arg: "confirm_token",
            type: "string",
          },
        ],
      },
    ],
  },
  {
    name: "greenzone",
    summary: "The Bridge4IP Greenzone whitelist",
    commands: [
      {
        name: "list",
        tool: "greenzone_whitelist_list",
        summary: "List current Greenzone whitelist entries from the KV cache.",
        options: [
          {
            flags: "--from-portal <bool>",
            description: "When true, return Kapture steps to read live portal state instead of KV cache.",
            arg: "from_portal",
            type: "boolean",
          },
        ],
      },
      {
        name: "add",
        tool: "greenzone_whitelist_add",
        summary: "Add a host or IP to the Greenzone whitelist. Needs Kapture running in the operator's own Chrome — without it the dry run still returns the portal steps to follow by hand.",
        write: true,
        options: [
          {
            flags: "--host <host>",
            description: "Hostname to whitelist (e.g. 'example.com').",
            arg: "host",
            type: "string",
          },
          {
            flags: "--ip <ip>",
            description: "IP address or CIDR to whitelist (e.g. '203.0.113.0/24').",
            arg: "ip",
            type: "string",
          },
        ],
      },
      {
        name: "remove",
        tool: "greenzone_whitelist_remove",
        summary: "Remove a host or IP from the Greenzone whitelist. Needs Kapture running in the operator's own Chrome — without it the dry run still returns the portal steps to follow by hand.",
        write: true,
        options: [
          {
            flags: "--host <host>",
            description: "Hostname to remove from whitelist.",
            arg: "host",
            type: "string",
          },
          {
            flags: "--ip <ip>",
            description: "IP address or CIDR to remove from whitelist.",
            arg: "ip",
            type: "string",
          },
        ],
        requireConfirm: true,
      },
    ],
  },
  {
    name: "agent",
    summary: "Steel browsing agents: one-off tasks and cron schedules",
    commands: [
      {
        name: "ask",
        tool: "ui_agent_ask",
        summary: "Send a plain-language task to a hosted browsing agent, which drives a real browser on the open web — navigating pages, searching, extracting content, filling forms outside the OCS portal.",
        write: true,
        options: [
          {
            flags: "--prompt <prompt>",
            description: "Natural-language task description for the browsing agent.",
            arg: "prompt",
            type: "string",
            required: true,
          },
          {
            flags: "--max-steps <n>",
            description: "Advisory step budget (1–30, default 15).",
            arg: "max_steps",
            type: "number",
          },
        ],
      },
      {
        name: "status",
        tool: "ui_agent_status",
        summary: "Poll the status of a browser agent task dispatched by ui_agent_ask or any ui_* tool.",
        options: [
          {
            flags: "--task-id <id>",
            description: "Task ID returned by ui_agent_ask or any ui_* tool dispatch.",
            arg: "task_id",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "pending",
        tool: "ui_agent_list_pending",
        summary: "Returns all browser automation tasks that are currently paused waiting for human input.",
        options: [],
      },
      {
        name: "reply",
        tool: "ui_agent_reply",
        summary: "Resumes a browser automation task that paused waiting for input.",
        options: [
          {
            flags: "--task-id <id>",
            description: "Task ID to resume (from ui_agent_list_pending).",
            arg: "task_id",
            type: "string",
            required: true,
          },
          {
            flags: "--reply <reply>",
            description: "Your answer to the agent's question (e.g. a 2FA code or confirmation).",
            arg: "reply",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "usage",
        tool: "ui_agent_usage",
        summary: "Returns an estimated Anthropic API cost for Steel agent tasks dispatched this calendar month.",
        options: [],
      },
      {
        name: "schedule list",
        tool: "ui_agent_schedule_list",
        summary: "Lists all Steel recurring schedules stored in KV.",
        options: [],
      },
      {
        name: "schedule create",
        tool: "ui_agent_schedule_create",
        summary: "Creates a recurring Steel agent run on a cron schedule.",
        requireConfirm: true,
        options: [
          {
            flags: "--name <name>",
            description: "Human-readable name for this schedule.",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--cron <cron>",
            description: "Standard 5-field cron expression (minute hour day month weekday).",
            arg: "cron",
            type: "string",
            required: true,
          },
          {
            flags: "--prompt-template <template>",
            description: "Agent prompt/task template the Steel agent will execute on each run.",
            arg: "prompt_template",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "schedule pause",
        tool: "ui_agent_schedule_pause",
        summary: "Pauses an active Steel recurring schedule.",
        requireConfirm: true,
        options: [
          {
            flags: "--schedule-id <id>",
            description: "ID of the schedule to pause.",
            arg: "schedule_id",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "schedule resume",
        tool: "ui_agent_schedule_resume",
        summary: "Resumes a paused Steel recurring schedule.",
        requireConfirm: true,
        options: [
          {
            flags: "--schedule-id <id>",
            description: "ID of the schedule to resume.",
            arg: "schedule_id",
            type: "string",
            required: true,
          },
        ],
      },
      {
        name: "schedule delete",
        tool: "ui_agent_schedule_delete",
        summary: "Permanently deletes a Steel recurring schedule.",
        requireConfirm: true,
        options: [
          {
            flags: "--schedule-id <id>",
            description: "ID of the schedule to delete.",
            arg: "schedule_id",
            type: "string",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "portal",
    summary: "OCS portal changes that only exist in the web UI, driven by Kapture",
    commands: [
      {
        name: "account create",
        tool: "ui_create_account",
        summary: "Creates a new sub-account under the reseller via the OCS web dashboard.",
        write: true,
        options: [
          {
            flags: "--name <name>",
            description: "Name for the new account.",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--description <description>",
            description: "Optional description.",
            arg: "description",
            type: "string",
          },
          {
            flags: "--initial-balance <n>",
            description: "Initial balance in account currency (default 0).",
            arg: "initial_balance",
            type: "number",
          },
        ],
        requireConfirm: true,
      },
      {
        name: "steering build",
        tool: "ui_build_steering_list",
        summary: "Change which mobile operators sit on a steering list that already exists, by driving the OCS web dashboard — the OCS REST API cannot edit list membership.",
        write: true,
        options: [
          {
            flags: "--steering-list-id <n>",
            description: "ID of the steering list to modify.",
            arg: "steering_list_id",
            type: "number",
            required: true,
          },
          {
            flags: "--add-operators <list>",
            description: "MCC-MNC codes to add (e.g. ['20801', '26201']). Comma-separated.",
            arg: "add_operators",
            type: "string[]",
          },
          {
            flags: "--remove-operators <list>",
            description: "MCC-MNC codes to remove. Comma-separated.",
            arg: "remove_operators",
            type: "string[]",
          },
          {
            flags: "--operator-type <type>",
            description: "Whether operators are priority or excluded. One of: priority, excluded. Default \"priority\".",
            arg: "operator_type",
            type: "string",
          },
        ],
      },
      {
        name: "steering create",
        tool: "ui_create_steering_list",
        summary: "Create a new, empty network steering list (OPLMN preference set) by driving the OCS web dashboard, because the OCS REST API cannot create one.",
        write: true,
        options: [
          {
            flags: "--name <name>",
            description: "Name for the new steering list.",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--description <description>",
            description: "Optional description for the steering list.",
            arg: "description",
            type: "string",
          },
        ],
      },
      {
        name: "steering set-account",
        tool: "ui_set_account_steering_list",
        summary: "Assigns or removes a steering list at the account level via the OCS web dashboard.",
        write: true,
        options: [
          {
            flags: "--account-id <n>",
            description: "Account ID to assign the steering list to.",
            arg: "account_id",
            type: "number",
            required: true,
          },
          {
            flags: "--steering-list-id <n>",
            description: "Steering list ID to assign (0 to remove/unset).",
            arg: "steering_list_id",
            type: "number",
            required: true,
          },
        ],
      },
      {
        name: "relay request",
        tool: "ui_request_reseller_relay_change",
        summary: "Drafts the parent-reseller/support request for reseller relay flags that are visible but read-only in the OCS portal at the current account tier.",
        options: [
          {
            flags: "--reseller-id <n>",
            description: "Reseller id; defaults to the authenticated reseller.",
            arg: "reseller_id",
            type: "number",
          },
          {
            flags: "--relay-gy <bool>",
            description: "Desired Relay Gy state.",
            arg: "relay_gy",
            type: "boolean",
          },
          {
            flags: "--relay-calls-sms <bool>",
            description: "Desired calls/SMS relay state.",
            arg: "relay_calls_sms",
            type: "boolean",
          },
          {
            flags: "--relay-voip <bool>",
            description: "Desired VoIP relay state.",
            arg: "relay_voip",
            type: "boolean",
          },
          {
            flags: "--reason <reason>",
            description: "Operational reason for the requested change.",
            arg: "reason",
            type: "string",
          },
        ],
      },
      {
        name: "destinations create",
        tool: "ui_create_destination_list",
        summary: "Creates a new destination list (named set of phone number prefixes for MOC call permissions) via the OCS web dashboard.",
        write: true,
        options: [
          {
            flags: "--name <name>",
            description: "Name for the new destination list.",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--prefixes <list>",
            description: "Phone number prefixes to include (e.g. ['+31', '+49']). Comma-separated.",
            arg: "prefixes",
            type: "string[]",
          },
          {
            flags: "--description <description>",
            description: "Optional description.",
            arg: "description",
            type: "string",
          },
        ],
      },
      {
        name: "destinations edit",
        tool: "ui_edit_destination_list",
        summary: "Edits an existing destination list via the OCS web dashboard.",
        write: true,
        options: [
          {
            flags: "--destination-list-id <n>",
            description: "ID of the destination list to edit.",
            arg: "destination_list_id",
            type: "number",
            required: true,
          },
          {
            flags: "--add-prefixes <list>",
            description: "Prefixes to add. Comma-separated.",
            arg: "add_prefixes",
            type: "string[]",
          },
          {
            flags: "--remove-prefixes <list>",
            description: "Prefixes to remove. Comma-separated.",
            arg: "remove_prefixes",
            type: "string[]",
          },
          {
            flags: "--new-name <name>",
            description: "Rename the destination list.",
            arg: "new_name",
            type: "string",
          },
        ],
      },
      {
        name: "destinations delete",
        tool: "ui_delete_destination_list",
        summary: "Delete a destination list — a named set of phone-number prefixes — by driving the OCS web dashboard.",
        write: true,
        options: [
          {
            flags: "--destination-list-id <n>",
            description: "ID of the destination list to delete.",
            arg: "destination_list_id",
            type: "number",
            required: true,
          },
        ],
        requireConfirm: true,
      },
      {
        name: "zones edit",
        tool: "ui_edit_location_zone",
        summary: "Edits an existing location zone via the OCS web dashboard. `create_location_zone` is available via API; edit is UI-only.",
        write: true,
        options: [
          {
            flags: "--zone-id <n>",
            description: "ID of the location zone to edit.",
            arg: "zone_id",
            type: "number",
            required: true,
          },
          {
            flags: "--new-name <name>",
            description: "Rename the location zone.",
            arg: "new_name",
            type: "string",
          },
          {
            flags: "--add-countries <list>",
            description: "ISO country codes to add (e.g. ['NL', 'DE']). Comma-separated.",
            arg: "add_countries",
            type: "string[]",
          },
          {
            flags: "--remove-countries <list>",
            description: "ISO country codes to remove. Comma-separated.",
            arg: "remove_countries",
            type: "string[]",
          },
        ],
      },
      {
        name: "zones delete",
        tool: "ui_delete_location_zone",
        summary: "Delete a location zone by driving the OCS web dashboard, because the OCS REST API can create a zone (`create_location_zone`) but not remove one.",
        write: true,
        options: [
          {
            flags: "--zone-id <n>",
            description: "ID of the location zone to delete.",
            arg: "zone_id",
            type: "number",
            required: true,
          },
        ],
        requireConfirm: true,
      },
      {
        name: "templates delete",
        tool: "ui_delete_package_template",
        summary: "Remove a package template from the reseller's product catalogue for good, by driving the OCS web dashboard — the OCS REST API cannot delete a template.",
        write: true,
        options: [
          {
            flags: "--template-id <n>",
            description: "ID of the package template to delete.",
            arg: "template_id",
            type: "number",
            required: true,
          },
        ],
        requireConfirm: true,
      },
    ],
  },
  {
    name: "events",
    summary: "Buffered OCS event history per subscriber",
    commands: [
      {
        name: "ocs",
        tool: "list_recent_ocs_events",
        summary: "Return the last N OCS events buffered for a given ICCID.",
        options: [
          {
            flags: "--iccid <iccid>",
            description: "ICCID, 19 or 20 digits, ITU-T E.118 format.",
            arg: "iccid",
            type: "string",
            required: true,
          },
          {
            flags: "--limit <n>",
            description: "Max events to return, newest first. Default 20.",
            arg: "limit",
            type: "number",
          },
          {
            flags: "--event-types <list>",
            description: "Filter to specific event types. Comma-separated.",
            arg: "event_types",
            type: "string[]",
          },
          {
            flags: "--since <since>",
            description: "ISO-8601 timestamp; only events after this point.",
            arg: "since",
            type: "string",
          },
        ],
      },
      {
        name: "countries",
        tool: "subscriber_country_history",
        summary: "Return cross-border movement history for a subscriber, reconstructed from OCS Gy charging events.",
        options: [
          {
            flags: "--subscriber-id <id>",
            description: "Subscriber ICCID (19–20 digits, ITU-T E.118).",
            arg: "subscriberId",
            type: "string",
            required: true,
          },
          {
            flags: "--start-date <date>",
            description: "Start of the period, YYYY-MM-DD inclusive (default: 7 days ago).",
            arg: "startDate",
            type: "string",
          },
          {
            flags: "--end-date <date>",
            description: "End of the period, YYYY-MM-DD inclusive, max 7 days from start (default: today).",
            arg: "endDate",
            type: "string",
          },
          {
            flags: "--limit <n>",
            description: "Maximum number of country_events to return, newest first (default 20, max 50). Default 20.",
            arg: "limit",
            type: "number",
          },
        ],
      },
      {
        name: "depletion",
        tool: "subscriber_depletion_events",
        summary: "Look up bundle depletion events for a subscriber (or list recent fleet-wide depletions).",
        options: [
          {
            flags: "--subscriber-id <id>",
            description: "OCS subscriber ID to look up.",
            arg: "subscriberId",
            type: "string",
          },
          {
            flags: "--since <since>",
            description: "ISO-8601 timestamp — only return events at or after this time (for single-subscriber lookup).",
            arg: "since",
            type: "string",
          },
        ],
      },
    ],
  },
  {
    name: "panels",
    summary: "MCP UI surfaces. A terminal sees the underlying data, not the panel.",
    commands: [
      {
        name: "fleet",
        tool: "fleet_health_app",
        summary: "Fleet overview as a UI panel. Prefer `carrier intelligence fleet-health` in a terminal; use --json to read the panel's data.",
        options: [
          {
            flags: "--account-id <n>",
            description: "Filter to a specific account (omit for all).",
            arg: "accountId",
            type: "number",
          },
        ],
      },
      {
        name: "topup",
        tool: "balance_topup_form",
        summary: "Adjust the OCS account balance behind one subscriber. Previews unless --preview false.",
        requireConfirm: true,
        options: [
          {
            flags: "--iccid <iccid>",
            description: "The subscriber ICCID for the account lookup.",
            arg: "iccid",
            type: "string",
            required: true,
          },
          {
            flags: "--delta <n>",
            description: "Amount to add (positive) or deduct (negative) from the account balance.",
            arg: "delta",
            type: "number",
            required: true,
          },
          {
            flags: "--preview <bool>",
            description: "If true, return preview without writing. Default true.",
            arg: "preview",
            type: "boolean",
          },
        ],
      },
      {
        name: "provision",
        tool: "provision_esim_wizard",
        summary: "Step through the provisioning wizard. Pass --step confirm to execute; every earlier step only reads.",
        options: [
          {
            flags: "--step <step>",
            description: "Current wizard step. One of: init, select-package, preview, confirm.",
            arg: "step",
            type: "string",
            required: true,
          },
          {
            flags: "--wizard-id <id>",
            description: "Wizard session ID (absent on init).",
            arg: "wizardId",
            type: "string",
          },
          {
            flags: "--subscriber-iccid <iccid>",
            description: "Subscriber ICCID (required for select-package).",
            arg: "subscriber_iccid",
            type: "string",
          },
          {
            flags: "--package-template-id <n>",
            description: "Package template ID (required for preview).",
            arg: "package_template_id",
            type: "number",
          },
        ],
      },
    ],
  },
  {
    name: "env",
    summary: "This environment: credentials, catalog and machine-readable context",
    commands: [
      {
        name: "info",
        tool: "environment_info",
        summary: "View your Carrier environment configuration — active organization, reseller details, connected services, and deployment environment.",
        options: [],
      },
      {
        name: "credentials",
        tool: "credential_status",
        summary: "Report on the stored eSIMVault credential without changing it: whether a token is present, whether it is encrypted, how old it is, and whether rotation is recommended (over 90 days).",
        options: [],
      },
      {
        name: "rotate",
        tool: "rotate_credentials",
        summary: "Replace the stored eSIMVault API token with one you supply.",
        options: [
          {
            flags: "--new-token <token>",
            description: "New eSIMVault API token to encrypt and store.",
            arg: "new_token",
            type: "string",
            required: true,
          },
          {
            flags: "--confirm <bool>",
            description: "Confirm rotation — this will replace the current token immediately.",
            arg: "confirm",
            type: "boolean",
            required: true,
          },
        ],
      },
      {
        name: "catalog",
        tool: "service_catalog",
        summary: "Browse the Carrier service catalog — discover available services, their requirements, endpoints, and documentation.",
        options: [
          {
            flags: "--category <category>",
            description: "Filter services by category. One of: connectivity, analytics, marketplace, coverage, billing, integration, dashboard.",
            arg: "category",
            type: "string",
          },
          {
            flags: "--tier <tier>",
            description: "Filter services accessible at this tier level. One of: free, pro, enterprise.",
            arg: "tier",
            type: "string",
          },
        ],
      },
      {
        name: "llm-context",
        tool: "llm_context",
        summary: "Generate a comprehensive LLM context document describing your Carrier environment, available tools, current tier, usage patterns, and best practices.",
        options: [
          {
            flags: "--format <format>",
            description: "Output format for the context document (default: markdown). One of: markdown, json, yaml.",
            arg: "format",
            type: "string",
          },
          {
            flags: "--include-examples <bool>",
            description: "Include example tool invocations (default: true).",
            arg: "include_examples",
            type: "boolean",
          },
        ],
      },
      {
        name: "describe",
        tool: "carrier_ask_describe",
        summary: "Get full documentation for any registered Carrier MCP tool: description, parameters, 2-3 example invocations, required scope, destructive flag, and guidance.",
        options: [
          {
            flags: "--tool-name <name>",
            description: "The exact MCP tool name to describe (e.g. 'assign_package', 'hlr_set_bitrate').",
            arg: "tool_name",
            type: "string",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "brand",
    summary: "Storefront brand assets",
    commands: [
      {
        name: "logo",
        tool: "generate_storefront_logo",
        summary: "Generate a storefront logo (SVG always; PNG when an image key is configured on the Carrier worker).",
        options: [
          {
            flags: "--name <name>",
            description: "Brand / shop name, e.g. Bananas.",
            arg: "name",
            type: "string",
            required: true,
          },
          {
            flags: "--accent <accent>",
            description: "Accent hex, e.g. #FF8A00.",
            arg: "accent",
            type: "string",
          },
          {
            flags: "--tagline <tagline>",
            description: "Optional tagline for AI-enhanced marks.",
            arg: "tagline",
            type: "string",
          },
        ],
      },
    ],
  },
  // </generated:catalog-domains>
];

/**
 * Every MCP tool the discrete commands can reach.
 *
 * REST-transport commands name no tool and are absent by design — the parity
 * test that consumes this must not expect them.
 */
export function capabilityToolNames(): string[] {
  return CLI_DOMAINS.flatMap((d) =>
    d.commands.filter((c) => c.transport !== "rest").map((c) => (c as McpCapabilityCommand).tool),
  );
}

/** Every REST endpoint the discrete commands can reach, as "METHOD /path". */
export function capabilityRestEndpoints(): Array<{ method: RestMethod; path: string }> {
  return CLI_DOMAINS.flatMap((d) =>
    d.commands
      .filter((c): c is RestCapabilityCommand => c.transport === "rest")
      .map((c) => ({ method: c.restMethod, path: c.restPath })),
  );
}

/** Label for a command in spinners, notes and errors. */
export function capabilityTarget(cmd: CapabilityCommand): string {
  return cmd.transport === "rest" ? `${cmd.restMethod} ${cmd.restPath}` : cmd.tool;
}
